import { Link } from 'react-router-dom';
import HeroOrbit from '../components/HeroOrbit';
import Layout from '../components/Layout';
import ProofStrip from '../components/ProofStrip';
import SectionHeader from '../components/SectionHeader';
import ServiceCard from '../components/ServiceCard';
import ToolStackMatrix from '../components/ToolStackMatrix';
import { Icon, IconId } from '../components/icons';
import { SERVICE_CARDS } from '../data/services';
import { SITE } from '../data/site';
import { usePageMeta } from '../hooks/usePageMeta';

const PAINS: { title: string; desc: string }[] = [
  {
    title: 'The pilot dies in the sandbox',
    desc: 'a demo impressed leadership, then stalled on security review, licensing, or data access.',
  },
  {
    title: 'People are the integration layer',
    desc: 'invoices re-keyed, customers entered into three systems, approvals lost in inboxes.',
  },
  {
    title: 'Governance arrives after launch',
    desc: 'audit trails, access control, and human review get bolted on once something breaks.',
  },
];

const OUTCOMES: { title: string; desc: string }[] = [
  {
    title: 'First automation live in 2–6 weeks',
    desc: 'fixed scope, real data, in production — not a proof of concept.',
  },
  {
    title: '25–40% efficiency gains',
    desc: 'on the workflows we automate, with exceptions routed to a human instead of a queue.',
  },
  {
    title: 'Governed from day one',
    desc: 'audit trails, role-based access, and Canadian data residency designed in, not added later.',
  },
];

const WHY_US_HIGHLIGHTS: { title: string; desc: string; icon: IconId }[] = [
  {
    title: 'Security-First Architecture',
    desc: 'AI built with enterprise security, compliance, and data boundaries from day one.',
    icon: 'security',
  },
  {
    title: 'Microsoft-Deep, Tool-Agnostic',
    desc: 'Specialists in Copilot Studio, Power Platform, and Azure AI — fluent in n8n and open stacks when they’re the better fit.',
    icon: 'microsoft',
  },
  {
    title: 'Small, Responsive Team',
    desc: 'No handoffs. No offshore teams. Senior consultants working directly with your team.',
    icon: 'advisory',
  },
];

function Home() {
  usePageMeta({
    title: `${SITE.name} - AI Automation & Microsoft Copilot Consulting`,
    description: SITE.description,
  });

  return (
    <Layout>
      {/* HERO SECTION */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <span className="section-label">AI CONSULTING &amp; AUTOMATION</span>
            <h1 className="hero-title">
              AI That Actually
              <br />
              <span className="hero-title-accent">Runs.</span>
            </h1>
            <p className="hero-description">
              We help organizations design, build, and run AI automation — from Microsoft Copilot
              and Power Platform to custom workflow agents that connect every system in between.
            </p>
            <div className="cta-buttons">
              <a
                href={SITE.calendly}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Book a Consultation
              </a>
              <Link to="/services" className="btn-secondary">
                Explore Services
              </Link>
            </div>
          </div>
          <div className="hero-visual">
            <HeroOrbit />
          </div>
        </div>
        <div className="hero-wave"></div>
      </section>

      {/* PROOF STRIP */}
      <ProofStrip />

      {/* PROBLEM → OUTCOME */}
      <section className="section section-white">
        <div className="container">
          <div className="section-header-center">
            <span className="section-label">WHY IT MATTERS</span>
            <h2 className="section-title">Most AI pilots never reach production.</h2>
            <p className="section-subtitle">
              The technology usually works. What fails is everything around it — data, governance,
              and the handoff to the people who have to live with it.
            </p>
          </div>

          <div className="problem-outcome reveal">
            <div className="problem-outcome-col">
              <span className="section-label">THE PROBLEM</span>
              <h3 className="problem-outcome-title">What we see when we arrive</h3>
              <ul className="problem-list">
                {PAINS.map(item => (
                  <li key={item.title}>
                    <span
                      className="problem-list-marker problem-list-marker-pain"
                      aria-hidden="true"
                    >
                      <Icon id="governance" size={13} />
                    </span>
                    <span>
                      <strong>{item.title}</strong> — {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="problem-outcome-col">
              <span className="section-label">WHAT CHANGES</span>
              <h3 className="problem-outcome-title">Automation that actually runs</h3>
              <ul className="problem-list">
                {OUTCOMES.map(item => (
                  <li key={item.title}>
                    <span
                      className="problem-list-marker problem-list-marker-win"
                      aria-hidden="true"
                    >
                      <Icon id="check" size={14} />
                    </span>
                    <span>
                      <strong>{item.title}</strong> — {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="contact-link">
                How we work, and who you&rsquo;ll work with &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="section section-gray">
        <div className="container">
          <SectionHeader
            eyebrow="OUR SERVICES"
            title="End-to-End AI Transformation"
            subtitle="Three services, one team. Production from day one."
          />
          <div className="services-grid reveal">
            {SERVICE_CARDS.map(service => (
              <ServiceCard key={service.title} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERY ARCHITECTURE (compact) */}
      <section className="section section-white">
        <div className="container">
          <SectionHeader
            eyebrow="DELIVERY ARCHITECTURE"
            title="Built On"
            subtitle="A connected stack across AI, automation, apps, and data."
          />
          <div className="reveal">
            <ToolStackMatrix compact />
          </div>
          <div className="section-more-link reveal">
            <Link to="/services" className="contact-link">
              See the full stack &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* APPROACH SECTION */}
      <section className="section section-gray">
        <div className="container">
          <SectionHeader
            eyebrow="OUR APPROACH"
            title="How We Work"
            subtitle="A proven, structured methodology that takes you from AI idea to production adoption."
          />

          <div className="approach-grid reveal">
            <div className="approach-step">
              <div className="approach-number">01</div>
              <h3 className="approach-title">Discover</h3>
              <p className="approach-desc">Understand business goals, workflows, and pain points</p>
            </div>
            <div className="approach-step">
              <div className="approach-number">02</div>
              <h3 className="approach-title">Assess</h3>
              <p className="approach-desc">Evaluate AI readiness, data security, and compliance</p>
            </div>
            <div className="approach-step">
              <div className="approach-number">03</div>
              <h3 className="approach-title">Design</h3>
              <p className="approach-desc">Architect scalable AI solutions and governance models</p>
            </div>
            <div className="approach-step">
              <div className="approach-number">04</div>
              <h3 className="approach-title">Build</h3>
              <p className="approach-desc">
                Develop Copilot agents, Power Platform solutions, and automation workflows
              </p>
            </div>
            <div className="approach-step">
              <div className="approach-number">05</div>
              <h3 className="approach-title">Deploy &amp; Adopt</h3>
              <p className="approach-desc">
                Roll out AI with training, monitoring, and optimization
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US SECTION (trimmed to 3) */}
      <section className="section section-white">
        <div className="container">
          <SectionHeader
            eyebrow="WHY CHOOSE US"
            title="Built for Outcomes, Not Slideware"
            subtitle="What makes us different from traditional dev shops and big-box consultancies."
          />

          <div className="numbered-grid reveal">
            {WHY_US_HIGHLIGHTS.map((item, i) => (
              <div className="numbered-item" key={item.title}>
                <div className="numbered-index">{String(i + 1).padStart(2, '0')}</div>
                <div
                  className={`feature-icon${item.icon === 'microsoft' ? ' feature-icon-product' : ''}`}
                >
                  <Icon id={item.icon} size={item.icon === 'microsoft' ? 28 : 24} />
                </div>
                <h4 className="feature-title">{item.title}</h4>
                <p className="feature-desc">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="section-more-link reveal">
            <Link to="/about" className="contact-link">
              See all 6 reasons &rarr;
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Home;
